import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { StatsTable } from '@fpsm/cxy-components/StatsTable';

import {
  type StatsTable as StatsTableType,
  type StatsTableColumn,
  type StatsTableRow,
} from '~/components/BettingInsights/types';

import styles from '../styles/Trends.module.scss';
import { formatStatValue } from '../utils/formatStatValue';

export type TrendType = 'last5' | 'last10' | 'last20';

export type TrendsStatsTableProps = {
  content: StatsTableType;
  formatDate: (date: string, format?: string) => string;
  highlightResults?: boolean;
  length: number;
  trend: TrendType;
};

export const TrendsStatsTable = ({
  content,
  formatDate,
  highlightResults = true,
  length,
  trend,
}: TrendsStatsTableProps) => {
  const PINNED_INDEX = 3;
  const columns = content.columns;
  const rows = content.rows.slice(0, Number(length));

  const columnTypes = columns.map((column: StatsTableColumn) => column.type);
  const columnAverages = columns.map((column: StatsTableColumn) => column.average && column.average[trend]);

  const getHighlight = (betResult?: string) => {
    if (!highlightResults) return undefined;
    if (betResult === 'WON') return 'green';
    if (betResult === 'LOST') return 'red';
    return undefined;
  };

  const { t } = useTranslation();

  useEffect(() => {
    if (typeof window === 'undefined') return;
    requestAnimationFrame(() => {
      window.dispatchEvent(new Event('resize'));
    });
  }, [length]);

  const columnHeadings = {
    date: t('DATE'),
    games: t('CX_PLUS_TRENDS_STATS_GAME'),
    result: t('CX_PLUS_TRENDS_STATS_RESULT'),
  };

  return (
    <StatsTable caption={t('CX_PLUS_TRENDS_STATS_TITLE')} className={styles.statsTable} maxRows={10}>
      <StatsTable.Head>
        <StatsTable.Row>
          {columns.map((column, index) => {
            const heading =
              typeof column.value === 'string' &&
              columnHeadings[column.value.toLowerCase() as keyof typeof columnHeadings];
            const label = heading || column.value;

            return (
              <StatsTable.Column
                justifyContent={index < PINNED_INDEX ? 'flex-start' : 'center'}
                key={`stats-table-${trend}-column-${index}`}
                pinned={index < PINNED_INDEX}
              >
                {label}
              </StatsTable.Column>
            );
          })}
        </StatsTable.Row>
      </StatsTable.Head>

      <StatsTable.Body>
        {rows.map((row: StatsTableRow, rowIndex: number) => (
          <StatsTable.Row key={`stats-table-${trend}-row-${rowIndex}`}>
            {row.cells.map((cell, index) => (
              <StatsTable.Cell
                highlight={getHighlight(cell.betResult)}
                justifyContent={index < PINNED_INDEX ? 'flex-start' : 'center'}
                key={`stats-table-${trend}-${rowIndex}-cell-${index}`}
                pinned={index < PINNED_INDEX}
              >
                {cell.value && formatStatValue(cell.value, columnTypes[index], formatDate)}
              </StatsTable.Cell>
            ))}
          </StatsTable.Row>
        ))}
      </StatsTable.Body>
      <StatsTable.Footer>
        <StatsTable.Row>
          {columnAverages.map((average, index) => (
            <React.Fragment key={`footer-cell-${trend}-${index}`}>
              {index === 0 ? (
                <StatsTable.Column pinned scope="row">
                  {t('CX_PLUS_TRENDS_STATS_AVERAGE')}
                </StatsTable.Column>
              ) : (
                <StatsTable.Cell justifyContent={index === 0 ? 'flex-start' : 'center'} pinned={index < PINNED_INDEX}>
                  {average && formatStatValue(average, columnTypes[index], formatDate)}
                </StatsTable.Cell>
              )}
            </React.Fragment>
          ))}
        </StatsTable.Row>
      </StatsTable.Footer>
    </StatsTable>
  );
};
